import React, { useState, useEffect, useMemo } from 'react';
import api from '../api/axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Lock, Search } from 'lucide-react';
import UnsavedChangesDialog, { useUnsavedChanges } from '../components/UnsavedChangesDialog';

export default function FuncionariosPage() {
  const { canManageCadastros } = useAuth();
  const canEdit = canManageCadastros();
  
  const [funcionarios, setFuncionarios] = useState([]);
  const [secretarias, setSecretarias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState({
    numero: '', name: '', admission_date: '', position: '', department: '', secretaria_id: '', status: 'active'
  });
  
  // Hook para controle de alterações não salvas
  const unsavedChanges = useUnsavedChanges();

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [funcionariosRes, secretariasRes] = await Promise.all([api.get('/funcionarios'), api.get('/secretarias')]);
      setFuncionarios(funcionariosRes.data);
      setSecretarias(secretariasRes.data);
    } catch (error) {
      toast.error('Erro ao carregar funcionários');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/funcionarios/${editingId}`, formData);
        toast.success('Funcionário atualizado!');
      } else {
        await api.post('/funcionarios', formData);
        toast.success('Funcionário cadastrado!');
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Erro ao salvar funcionário');
    }
  };

  const handleEdit = (item) => {
    setFormData(item);
    setEditingId(item.id);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deseja realmente excluir?')) return;
    try {
      await api.delete(`/funcionarios/${id}`);
      toast.success('Funcionário excluído!');
      fetchData();
    } catch (error) {
      toast.error('Erro ao excluir');
    }
  };

  const resetForm = () => {
    setFormData({ numero: '', name: '', admission_date: '', position: '', department: '', secretaria_id: '', status: 'active' });
    setEditingId(null);
    unsavedChanges.resetChanges();
  };

  // Função para lidar com mudanças no formulário
  const handleFormChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    unsavedChanges.markAsChanged();
  };

  // Função para tentar fechar o dialog
  const handleDialogClose = (open) => {
    if (!open && unsavedChanges.hasChanges) {
      unsavedChanges.tryAction(() => {
        setDialogOpen(false);
        resetForm();
      });
    } else {
      setDialogOpen(open);
      if (!open) resetForm();
    }
  };

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#003366]"></div></div>;

  const filteredFuncionarios = funcionarios
    .filter(f => {
      if (!searchQuery) return true;
      const q = searchQuery.toLowerCase();
      return (f.name || '').toLowerCase().includes(q) || (f.position || '').toLowerCase().includes(q) || (f.numero || '').toString().includes(q);
    })
    .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'pt-BR'));

  return (
    <div className="space-y-6" data-testid="funcionarios-page">
      {/* Diálogo de confirmação para sair sem salvar */}
      <UnsavedChangesDialog
        isOpen={unsavedChanges.showDialog}
        onOpenChange={unsavedChanges.setShowDialog}
        onConfirmLeave={unsavedChanges.confirmLeave}
      />
      
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold text-[#003366]" style={{ fontFamily: 'Manrope, sans-serif' }}>Funcionários</h2>
          <p className="text-slate-600">Gerenciamento de funcionários</p>
        </div>
        {canEdit ? (
          <Dialog open={dialogOpen} onOpenChange={handleDialogClose}>
            <DialogTrigger asChild>
              <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="bg-[#0066CC] hover:bg-[#0052A3]" data-testid="add-funcionario-button">
                <Plus className="mr-2" size={20} />Novo Funcionário
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>{editingId ? 'Editar' : 'Novo'} Funcionário</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="funcionario-form">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Número *</Label>
                  <Input value={formData.numero} onChange={(e) => handleFormChange('numero', e.target.value)} required data-testid="funcionario-numero-input" />
                </div>
                <div className="space-y-2">
                  <Label>Nome *</Label>
                  <Input value={formData.name} onChange={(e) => handleFormChange('name', e.target.value)} required data-testid="funcionario-name-input" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Data de Admissão *</Label>
                  <Input type="date" value={formData.admission_date} onChange={(e) => handleFormChange('admission_date', e.target.value)} required data-testid="funcionario-admission-input" />
                </div>
                <div className="space-y-2">
                  <Label>Cargo *</Label>
                  <Input value={formData.position} onChange={(e) => handleFormChange('position', e.target.value)} required data-testid="funcionario-position-input" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Departamento *</Label>
                  <Input value={formData.department} onChange={(e) => setFormData({ ...formData, department: e.target.value })} required data-testid="funcionario-department-input" />
                </div>
                <div className="space-y-2">
                  <Label>Secretaria</Label>
                  <select value={formData.secretaria_id} onChange={(e) => setFormData({ ...formData, secretaria_id: e.target.value })} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="funcionario-secretaria-select">
                    <option value="">Selecione...</option>
                    {secretarias.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Status *</Label>
                <select value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="funcionario-status-select">
                  <option value="active">Ativo</option>
                  <option value="inactive">Inativo</option>
                </select>
              </div>
              <div className="flex gap-2 justify-end">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
                <Button type="submit" className="bg-[#003366] hover:bg-[#002244]" data-testid="funcionario-submit-button">Salvar</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        ) : (
          <div className="flex items-center gap-2 text-amber-600 bg-amber-50 px-4 py-2 rounded-lg">
            <Lock size={18} />
            <span className="text-sm font-medium">Modo Visualização</span>
          </div>
        )}
      </div>

      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>Lista de Funcionários</CardTitle>
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
              <Input placeholder="Buscar por nome, cargo..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-9" data-testid="search-funcionarios" />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nº</TableHead>
                <TableHead>Nome</TableHead>
                <TableHead>Cargo</TableHead>
                <TableHead>Departamento</TableHead>
                <TableHead>Admissão</TableHead>
                <TableHead>Status</TableHead>
                {canEdit && <TableHead className="text-right">Ações</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredFuncionarios.map((item) => (
                <TableRow key={item.id} className="table-row" data-testid={`funcionario-row-${item.id}`}>
                  <TableCell className="font-medium">{item.numero}</TableCell>
                  <TableCell>{item.name}</TableCell>
                  <TableCell>{item.position}</TableCell>
                  <TableCell>{item.department}</TableCell>
                  <TableCell>{item.admission_date ? item.admission_date.split('-').reverse().join('/') : '-'}</TableCell>
                  <TableCell>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${item.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {item.status === 'active' ? 'Ativo' : 'Inativo'}
                    </span>
                  </TableCell>
                  {canEdit && (
                    <TableCell className="text-right">
                      <div className="flex gap-2 justify-end">
                        <Button size="sm" variant="outline" onClick={() => handleEdit(item)} data-testid={`edit-funcionario-${item.id}`}><Pencil size={16} /></Button>
                        <Button size="sm" variant="destructive" onClick={() => handleDelete(item.id)} data-testid={`delete-funcionario-${item.id}`}><Trash2 size={16} /></Button>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {filteredFuncionarios.length === 0 && (
                <TableRow><TableCell colSpan={canEdit ? 7 : 6} className="text-center py-8 text-slate-500">{searchQuery ? 'Nenhum resultado encontrado' : 'Nenhum funcionário cadastrado'}</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
